import {hframe} from '@xh/hoist/cmp/layout';
import {creates, hoistCmp} from '@xh/hoist/core';
import {button} from '@xh/hoist/desktop/cmp/button';
import {dashCanvas} from '@xh/hoist/desktop/cmp/dash';
import {menuButton} from '@xh/hoist/desktop/cmp/menu';
import {panel} from '@xh/hoist/desktop/cmp/panel';
import {toolbar} from '@xh/hoist/desktop/cmp/toolbar';
import {viewManager} from '@xh/hoist/desktop/cmp/viewmanager';
import {Icon} from '@xh/hoist/icon';
import {uniq} from 'lodash';
import {aiButton} from '../../cmp/ai/AiButton';
import {securityPicker} from '../../cmp/securityPicker/SecurityPicker';
import {specAssistant} from '../../cmp/spec/assistant/SpecAssistant';
import {RESEARCH_PRESETS} from './ResearchPresets';
import {ResearchTabModel} from './ResearchTabModel';
import './ResearchTab.scss';

export const researchTab = hoistCmp.factory<ResearchTabModel>({
    model: creates(ResearchTabModel),
    render({model}) {
        return panel({
            tbar: tbar(),
            item: hframe(dashCanvas(), assistantPanel({omit: !model.showAssistant})),
            mask: [model.ensureTask, model.viewManagerModel.selectTask]
        });
    }
});

const tbar = hoistCmp.factory<ResearchTabModel>({
    render({model}) {
        return toolbar({
            compact: true,
            items: [
                securityPicker({
                    ticker: model.ticker,
                    keepSelection: true,
                    showSearchIcon: true,
                    placeholder: 'Select ticker',
                    onSecuritySelected: s => model.loadResearch(s.ticker)
                }),
                viewManager({
                    model: model.viewManagerModel,
                    saveButtonProps: {text: 'Save'},
                    showRevertButton: 'whenDirty',
                    revertButtonProps: {text: 'Revert'},
                    extraMenuItems: ['-', {text: 'New dashboard...', icon: Icon.add(), actionFn: () => model.newDashboardAsync()}]
                }),
                button({
                    text: 'New Dashboard',
                    icon: Icon.add(),
                    intent: 'success',
                    disabled: model.isEditable,
                    onClick: () => model.newDashboardAsync()
                }),
                button({
                    text: 'Edit Dashboard',
                    icon: Icon.edit(),
                    minimal: !model.isEditable,
                    intent: 'primary',
                    onClick: () => model.toggleEditable()
                }),
                addMenu({omit: !model.isEditable}),
                aiButton({
                    text: 'Assistant',
                    title: 'Build or change this dashboard with the AI assistant',
                    active: model.showAssistant,
                    busy: model.assistantModel.task.isPending,
                    onClick: () => model.toggleAssistant()
                }),

                button({
                    icon: Icon.refresh(),
                    title: 'Reload research data',
                    disabled: !model.readyTicker,
                    onClick: () => model.refresh()
                })
            ]
        });
    }
});

/** The AI dashboard assistant, docked beside the canvas so its changes show as they are applied. */
const assistantPanel = hoistCmp.factory<ResearchTabModel>({
    render({model}) {
        return panel({
            className: 'research-assistant',
            modelConfig: {side: 'right', defaultSize: 340, minSize: 280, maxSize: 600, collapsible: false},
            item: specAssistant({model: model.assistantModel})
        });
    }
});

/** Adds an empty grid or chart, or one of the ready-made widgets. */
const addMenu = hoistCmp.factory<ResearchTabModel>({
    render({model}) {
        const groups = uniq(RESEARCH_PRESETS.map(p => p.group));

        return menuButton({
            text: 'Add Data',
            icon: Icon.add(),
            menuPosition: 'bottom-right',
            menuItems: [
                {text: 'Grid', icon: Icon.grid(), actionFn: () => model.addWidget('grid')},
                {text: 'Chart', icon: Icon.chartLine(), actionFn: () => model.addWidget('chart')},
                {heading: 'Ready-made'},
                ...groups.map(group => ({
                    text: group,
                    items: RESEARCH_PRESETS.filter(p => p.group === group).map(preset => ({
                        text: preset.title,
                        icon: preset.kind === 'grid' ? Icon.grid() : Icon.chartLine(),
                        actionFn: () => model.addPreset(preset)
                    }))
                }))
            ]
        });
    }
});
